import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Home, Package, Settings,
  Plus, AlertTriangle, Star,
  Loader2, Milestone, Info, MapPin
} from 'lucide-react';
import { customerAssetsApi } from '../../services/api';
import { industryService } from '../../services/industryService';
import { 
  industryUsesAssets, 
  getAssetTypesForIndustry,
  getAssetFieldSchema,
  parseAttributes,
  AssetTypes 
} from '../../config/assetTemplates';
import AssetForm from './AssetForm';

/**
 * AssetSelectorSection - Unified service location + property selector for JobFormModal
 *
 * When industry uses assets (e.g. PressureWashing → Property):
 *   - Replaces the separate "Property" dropdown + "Service Location" section
 *   - Shows radio buttons: customer's primary address + each saved property
 *   - Selecting a property updates the job's service address automatically
 *
 * When industry doesn't use assets:
 *   - Returns null (JobFormModal shows its own Service Location section)
 *
 * Props:
 * - customerId: Selected customer ID
 * - customerName: Customer name (for new asset form)
 * - selectedAssetId: Currently selected asset ID
 * - onAssetChange: Callback (assetId, asset, extraData)
 * - onAddressChange: Callback (addressString | null) — null means revert to customer address
 * - customerAddress: { address, city, state, zipCode } from customer record
 * - extraJobData: Industry-specific fields { mileageIn, behaviorNotes, etc. }
 * - onExtraDataChange: Callback for industry-specific field changes
 * - disabled: Disable all inputs
 */
const AssetSelectorSection = ({
  customerId,
  customerName = '',
  selectedAssetId,
  onAssetChange,
  onAddressChange,
  onUsesAssets,
  customerAddress = {},
  extraJobData = {},
  onExtraDataChange,
  autoSelectPrimary = false,
  assetPatch = null,
  onSelectedAssetResolved,
  disabled = false,
}) => {
  const [industryType, setIndustryType] = useState('Other');

  useEffect(() => {
    const fetchIndustry = async () => {
      try {
        const theme = await industryService.getMyTheme();
        setIndustryType(theme?.serviceType || 'Other');
      } catch (err) {
        console.log('Could not fetch industry:', err);
      }
    };
    fetchIndustry();
  }, []);

  const usesAssets = industryUsesAssets(industryType);
  const availableAssetTypes = getAssetTypesForIndustry(industryType);
  const primaryAssetType = availableAssetTypes[0] || null;

  useEffect(() => {
    if (industryType !== 'Other') {
      onUsesAssets?.(usesAssets);
    }
  }, [usesAssets, industryType]);

  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [showAssetForm, setShowAssetForm] = useState(false);

  const selectedAsset = assets.find(a => a.id === selectedAssetId) || null;
  const selectedAssetAttrs = selectedAsset ? parseAttributes(selectedAsset.attributesJson) : {};

  const loadAssets = useCallback(async () => {
    if (!customerId || !usesAssets) {
      setAssets([]);
      setLoaded(true);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const response = await customerAssetsApi.getByCustomer(customerId);
      const loadedAssets = response.data || [];
      setAssets(loadedAssets);
    } catch (err) {
      console.error('Error loading assets:', err);
      setError('Failed to load properties');
      setAssets([]);
    } finally {
      setLoading(false);
      setLoaded(true);
    }
  }, [customerId, usesAssets]);

  useEffect(() => {
    setLoaded(false);
    loadAssets();
  }, [loadAssets]);

  // Apply an attribute change made elsewhere (the quote calculator saving sq ft) without reloading the list
  useEffect(() => {
    if (!assetPatch) return;
    setAssets(prev => prev.map(a => (a.id === assetPatch.id ? { ...a, attributesJson: assetPatch.attributesJson } : a)));
  }, [assetPatch]);

  // Report the selected property to the parent. This also covers an existing job's saved property,
  // which is selected without anyone clicking it.
  useEffect(() => {
    onSelectedAssetResolved?.(selectedAsset);
  }, [selectedAsset?.id, selectedAsset?.attributesJson]);

  // Handle selecting a property or "customer's address" (assetId = null)
  const handleAssetSelect = (assetId, asset = null, assetList = assets) => {
    const selectedAssetObj = asset || assetList.find(a => a.id === assetId) || null;

    let extraData = {};

    if (selectedAssetObj) {
      const attrs = parseAttributes(selectedAssetObj.attributesJson);

      switch (selectedAssetObj.assetType) {
        case AssetTypes.Property:
          if (attrs.squareFeet) extraData.propertySqFt = attrs.squareFeet;
          if (attrs.drivewaySquareFeet) extraData.drivewaySqFt = attrs.drivewaySquareFeet;
          // Pass street, city, state and zip, or null if this property has no address stored
          onAddressChange?.(
            attrs.address
              ? {
                  address: attrs.address,
                  city: attrs.city || '',
                  state: attrs.state || '',
                  zipCode: attrs.zipCode || '',
                }
              : null
          );
          break;

        case AssetTypes.Equipment:
          // Equipment doesn't change the service address
          break;

        default:
          break;
      }
    } else {
      // "Customer's address" selected — revert
      onAddressChange?.(null);
    }

    onAssetChange?.(assetId, selectedAssetObj, extraData);
  };

  const handleAssetCreated = async (newAssetData) => {
    try {
      const response = await customerAssetsApi.create(customerId, newAssetData);
      const newAsset = response.data;
      await loadAssets();
      handleAssetSelect(newAsset.id, newAsset);
      setShowAssetForm(false);
    } catch (err) {
      console.error('Error creating asset:', err);
      throw err;
    }
  };

  // New jobs: pre-select the customer's primary Property once per customer.
  // The customerId filter stops a previous customer's properties being picked while the list reloads.
  const autoSelectedForRef = useRef(null);
  useEffect(() => {
    if (!autoSelectPrimary || !customerId || !loaded || loading || selectedAssetId || disabled) return;
    if (autoSelectedForRef.current === customerId) return;

    const props = assets.filter(a => a.customerId === customerId && a.assetType === AssetTypes.Property);
    const primary =
      props.find(a => a.isPrimary && parseAttributes(a.attributesJson).source === 'customer') ||
      props.find(a => a.isPrimary);

    if (primary) {
      autoSelectedForRef.current = customerId;
      handleAssetSelect(primary.id, primary, assets);
    }
  }, [autoSelectPrimary, customerId, loaded, loading, assets, selectedAssetId, disabled]);

  // Don't render if industry doesn't use assets or no customer selected
  if (!usesAssets || !customerId) return null;

  // Once the customer's address exists as a Property, the plain-text "Primary address" radio is redundant
  const hasCustomerProperty = assets.some(
    a => a.assetType === AssetTypes.Property && parseAttributes(a.attributesJson).source === 'customer'
  );

  const primaryCustomerLabel = customerAddress.address
    ? customerAddress.address
    : 'Customer\'s address';

  const primaryCustomerSub = [customerAddress.city, customerAddress.state, customerAddress.zipCode]
    .filter(Boolean).join(', ');

  return (
    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <label className="label mb-0 flex items-center">
          <MapPin size={16} className="mr-1.5 text-gray-500" />
          Service Location
        </label>
      </div>

      {/* Loading */}
      {loading && !loaded && (
        <div className="flex items-center gap-2 text-gray-500 text-sm py-2">
          <Loader2 size={16} className="animate-spin" />
          Loading properties...
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="text-red-600 text-sm py-2">{error}</div>
      )}

      {loaded && !loading && (
        <>
          <div className="space-y-2">

            {/* Option 1: legacy plain-text primary address, only until the customer has a Property */}
            {!hasCustomerProperty && (
            <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
              !selectedAssetId
                ? 'bg-white border-primary-300 ring-1 ring-primary-200'
                : 'bg-white border-gray-200 hover:border-gray-300'
            }`}>
              <input
                type="radio"
                name="serviceLocation"
                checked={!selectedAssetId}
                onChange={() => handleAssetSelect(null)}
                disabled={disabled}
                className="mt-0.5 text-primary-600"
              />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">{primaryCustomerLabel}</p>
                {primaryCustomerSub && (
                  <p className="text-xs text-gray-500">{primaryCustomerSub}</p>
                )}
                <p className="text-xs text-gray-400 mt-0.5">Primary address</p>
              </div>
            </label>
            )}

            {/* Option 2+: Each saved property */}
            {assets.map(asset => {
              const attrs = parseAttributes(asset.attributesJson);
              const isSelected = selectedAssetId === asset.id;
              const propSub = [
                attrs.propertyType,
                attrs.stories ? `${attrs.stories} story` : null,
                attrs.squareFeet ? `${Number(attrs.squareFeet).toLocaleString()} sq ft` : null,
              ].filter(Boolean).join(' • ');

              return (
                <label key={asset.id} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  isSelected
                    ? 'bg-white border-primary-300 ring-1 ring-primary-200'
                    : 'bg-white border-gray-200 hover:border-gray-300'
                }`}>
                  <input
                    type="radio"
                    name="serviceLocation"
                    checked={isSelected}
                    onChange={() => handleAssetSelect(asset.id, asset)}
                    disabled={disabled}
                    className="mt-0.5 text-primary-600"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 flex items-center gap-1">
                      {attrs.address || asset.name}
                      {asset.isPrimary && (
                        <Star size={11} className="text-amber-500" fill="currentColor" />
                      )}
                    </p>
                    {attrs.address && asset.name && asset.name !== attrs.address && (
                      <p className="text-xs text-gray-500">{asset.name}</p>
                    )}
                    {[attrs.city, attrs.state, attrs.zipCode].filter(Boolean).length > 0 && (
                      <p className="text-xs text-gray-500">
                        {[attrs.city, attrs.state, attrs.zipCode].filter(Boolean).join(', ')}
                      </p>
                    )}
                    {propSub && (
                      <p className="text-xs text-gray-400">{propSub}</p>
                    )}
                  </div>
                </label>
              );
            })}

            {/* Add another property */}
            <button
              type="button"
              onClick={() => setShowAssetForm(true)}
              disabled={disabled}
              className="w-full p-2.5 rounded-lg border border-dashed border-gray-300 text-sm text-gray-500 hover:border-gray-400 hover:text-gray-700 flex items-center justify-center gap-2 transition-colors"
            >
              <Plus size={14} />
              Add another property
            </button>
          </div>

          {/* Selected property details — gate code, warnings, etc. */}
          {selectedAsset && (
            <div className="mt-3">
              <AssetWarnings asset={selectedAsset} attrs={selectedAssetAttrs} />
              <PropertyDetails attrs={selectedAssetAttrs} />
              <IndustrySpecificFields
                asset={selectedAsset}
                attrs={selectedAssetAttrs}
                extraJobData={extraJobData}
                onExtraDataChange={onExtraDataChange}
                disabled={disabled}
              />
            </div>
          )}
        </>
      )}

      {/* Inline Asset Form */}
      {showAssetForm && (
        <AssetForm
          isOpen={showAssetForm}
          onClose={() => setShowAssetForm(false)}
          onSave={handleAssetCreated}
          asset={null}
          defaultAssetType={primaryAssetType}
          availableAssetTypes={availableAssetTypes}
          customerName={customerName}
        />
      )}
    </div>
  );
};

/**
 * Shows useful access info for the selected property inline
 */
const PropertyDetails = ({ attrs }) => {
  const details = [];
  if (attrs.gateCode) details.push({ label: 'Gate Code', value: attrs.gateCode });
  if (attrs.lockboxCode) details.push({ label: 'Lockbox', value: attrs.lockboxCode });
  if (attrs.accessNotes) details.push({ label: 'Access Notes', value: attrs.accessNotes });

  if (details.length === 0) return null;

  return (
    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-1">
      {details.map((d, i) => (
        <div key={i} className="flex items-start gap-2 text-xs">
          <span className="text-blue-500 font-medium flex-shrink-0">{d.label}:</span>
          <span className="text-blue-800">{d.value}</span>
        </div>
      ))}
    </div>
  );
};

/**
 * Warning banners for asset concerns
 */
const AssetWarnings = ({ asset, attrs }) => {
  const warnings = [];

  switch (asset.assetType) {
    case AssetTypes.Equipment:
      if (attrs.warrantyExpires) {
        const warrantyDate = new Date(attrs.warrantyExpires);
        if (warrantyDate < new Date()) {
          warnings.push({ type: 'warning', icon: Info, message: 'Warranty expired' });
        }
      }
      break;
    default:
      break;
  }

  if (warnings.length === 0) return null;

  return (
    <div className="mb-2 space-y-1">
      {warnings.map((w, i) => {
        const bgColor = w.type === 'danger' ? 'bg-red-50 border-red-200' :
                        w.type === 'warning' ? 'bg-amber-50 border-amber-200' :
                        'bg-blue-50 border-blue-200';
        const textColor = w.type === 'danger' ? 'text-red-700' :
                          w.type === 'warning' ? 'text-amber-700' :
                          'text-blue-700';
        const Icon = w.icon;
        return (
          <div key={i} className={`flex items-start gap-2 p-2 rounded-lg border ${bgColor}`}>
            <Icon size={14} className={`${textColor} mt-0.5 flex-shrink-0`} />
            <span className={`text-xs ${textColor}`}>{w.message}</span>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Industry-specific job fields (mileage for vehicles, behavior notes for pets, etc.)
 */
const IndustrySpecificFields = ({ asset, attrs, extraJobData, onExtraDataChange, disabled }) => {
  if (!asset) return null;

  switch (asset.assetType) {
    case AssetTypes.Equipment:
      return (
        <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <label className="label text-xs text-gray-600 mb-1">Service Notes</label>
          <input
            type="text"
            value={extraJobData.serviceNotes || ''}
            onChange={(e) => onExtraDataChange?.({ ...extraJobData, serviceNotes: e.target.value })}
            disabled={disabled}
            className="input text-sm py-1.5"
            placeholder="Any notes specific to this equipment today..."
          />
        </div>
      );

    default:
      return null;
  }
};

export default AssetSelectorSection;